/**
 * «قبل ما تكمل» — the legal texts, asked right after sign-in (المرحلة 1.1, P24).
 *
 * The sign-in answer carries the texts this person still has to accept: the
 * terms and the privacy notice in force for their institution (or the
 * platform's), when the `consentTexts` policy asks for them at all — and asks
 * again only when a new version was published and `reaccept` says so. Nothing
 * else on the screen can be used until they accept or leave; the acceptance
 * is stored with the version, the time, the address and the device.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Button, Icon } from '../../components/common';
import { authApi } from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n/I18nProvider';
import { formatDate } from '../../domain/locale';

const KIND_TONE = { terms: 'cyan', privacy: 'purple', telehealth: 'teal' };
const KIND_ICON = { terms: 'scale', privacy: 'lock-keyhole', telehealth: 'video' };

export function ConsentGate() {
  const { consentPending, consentDone, logout } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [ticked, setTicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [texts, setTexts] = useState(consentPending);

  // What the sign-in said may be stale after a reload: ask the server once.
  useEffect(() => {
    if (!consentPending.length) return;
    let alive = true;
    authApi
      .consent()
      .then((answer) => {
        if (!alive) return;
        if (!answer.pending || !answer.pending.length) consentDone();
        else setTexts(answer.pending);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentPending.length]);

  if (!consentPending.length || !texts.length) return null;

  const updated = texts.some((text) => text.version > 1);
  const allTicked = texts.every((text) => ticked[text.id]);
  const titleOf = (text) => (lang === 'en' && text.titleEn ? text.titleEn : text.title);
  const bodyOf = (text) => (lang === 'en' && text.bodyEn ? text.bodyEn : text.body);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await authApi.acceptConsent(texts.map((text) => text.id), 'login');
      consentDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const leave = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const dialog = (
    <div className="consent-gate fixed inset-0 z-[260] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="consent-gate-title" data-testid="consent-gate">
      <div className="glass-panel consent-gate__card relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl">
        <header className="consent-gate__head p-6 pb-4">
          <span className="tile3d__face tone-cyan mb-3" style={{ width: 52, height: 52 }}>
            <Icon name="shield-check" size={26} className="tile3d__icon" />
          </span>
          <h2 id="consent-gate-title" className="text-xl font-black text-white">
            {t('consent.gateTitle')}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-300">{updated ? t('consent.gateIntroUpdated') : t('consent.gateIntro')}</p>
        </header>

        <div className="custom-scrollbar flex-1 space-y-3 overflow-y-auto px-6 pb-2">
          {texts.map((text) => {
            const tone = KIND_TONE[text.kind] || 'cyan';
            const expanded = open === text.id;
            return (
              <article key={text.id} className={`consent-text tone-${tone} ${ticked[text.id] ? 'is-ticked' : ''}`}>
                <div className="flex items-start gap-3">
                  <span className="tile3d__face shrink-0" style={{ width: 40, height: 40 }}>
                    <Icon name={KIND_ICON[text.kind] || 'file-text'} size={19} className="tile3d__icon" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-white">{titleOf(text)}</p>
                    <p className="mt-0.5 text-[11px] text-slate-400">
                      {t('consent.version', { n: text.version })} · {t('consent.publishedOn', { when: formatDate(text.publishedAt) })}
                    </p>
                    {text.changeNote && <p className="mt-1 text-xs text-amber-300">{t('consent.whatChanged', { note: text.changeNote })}</p>}
                  </div>
                  <button type="button" className="consent-text__toggle" onClick={() => setOpen(expanded ? null : text.id)} aria-expanded={expanded}>
                    <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={15} />
                    {expanded ? t('consent.hide') : t('consent.read')}
                  </button>
                </div>
                {expanded && <div className="consent-text__body custom-scrollbar">{bodyOf(text)}</div>}
                <label className="consent-text__tick">
                  <input
                    type="checkbox"
                    checked={Boolean(ticked[text.id])}
                    onChange={(event) => setTicked((current) => ({ ...current, [text.id]: event.target.checked }))}
                  />
                  <span>{t('consent.agreeTo', { title: titleOf(text) })}</span>
                </label>
              </article>
            );
          })}
          {error && (
            <p className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs font-bold text-red-300">
              <Icon name="alert-circle" size={14} className="me-1 inline" />
              {error}
            </p>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 p-5">
          <button type="button" onClick={leave} className="text-sm font-bold text-slate-400 hover:text-red-300">
            <Icon name="log-out" size={15} className="me-1 inline" />
            {t('consent.decline')}
          </button>
          <Button icon="circle-check" onClick={accept} loading={busy} disabled={!allTicked} data-testid="consent-accept">
            {t('consent.accept')}
          </Button>
        </footer>
      </div>
    </div>
  );
  return typeof document === 'undefined' ? dialog : createPortal(dialog, document.body);
}

export default ConsentGate;
